import { derived, get, writable, type Readable } from 'svelte/store';
import { CART_ANIMATION_DURATION } from '../../constants/cart-animation-duration';
import type { CheckoutMixpanelEvents } from '../../types';
import { getDropTrackingFields } from '../getDropTrackingFields/getDropTrackingFields';
import { getTrackedCartItems } from '../getTrackedCartItems';
import { tagAddToCart } from '../tagAddToCart';
import { tagRemoveFromCart } from '../tagRemoveFromCart/tagRemoveFromCart';
import type { ContentKey } from '$global/utils/contentKey';
import { safePersistedStore } from '$global/utils/safePersistedStore/safePersistedStore';
import { track } from '$global/utils/track';

export type CartItem = {
	entityId: string;
	quantity: number;
	restricted: boolean;
	group: string | null | undefined;
	currency: string | null | undefined;
	sku: string | null | undefined;
	price: number | null | undefined;
	salePrice?: number | null | undefined;
	name: string | null | undefined;
	groupId: string | null | undefined;
	dropConfigId: string | null | undefined;
	sort?: number;
	// Global content key (HB-959). Optional because it rides on a store that is
	// persisted to localStorage — carts saved before this shipped, and sites
	// that cannot supply a field, must keep working untouched.
	contentKey?: ContentKey;
};

export type CartItems = Record<string, CartItem>;
export type CartItemsStore = ReturnType<typeof createCartItemsStore>;
export type Code = { code: string; amount: number; products: string[] };

export const createCartItemsStore = (key: string) => {
	const animated = writable('');
	const added = writable('');
	const cartItems = safePersistedStore(`${key}-cart-items`, {} as CartItems);
	const cartId = safePersistedStore(`${key}-cart-id`, '');
	const code = safePersistedStore(`${key}-code`, null as null | Code);
	const submittingCode = writable(false);
	const enablingCode = writable(false);

	// TODO checkout : find a way to test this block
	// debouncedCartItems remain undefined after advancing the timers
	const debouncedCartItems = derived(cartItems, ($cartItems, set) => {
		const timeoutId = setTimeout(() => {
			return set($cartItems);
		}, 1000);

		return () => clearTimeout(timeoutId);
	}) as undefined | Readable<CartItems>;

	const clearCode = () => {
		code.set(null);
	};

	const getCouponCode = () => (code ? get(code)?.code || null : null);
	const getCouponProducts = () => (code ? get(code)?.products || [] : []);
	const getCouponAmount = () => (code ? get(code)?.amount || null : null);

	const addCode = (newCode: Code) => {
		code.set(newCode);
	};

	const clearCart = () => {
		cartId.set('');
		cartItems.set({} as CartItems);
		code.set(null);
	};

	const resetCartWithItems = (items: CartItems) => {
		cartItems.set(items);
	};

	const refreshFromStorage = () => {
		try {
			const stored = localStorage.getItem(`${key}-cart-items`);
			if (stored) {
				cartItems.set(JSON.parse(stored));
			}
		} catch {
			// localStorage may not be available
		}
	};

	const totalNbItems = derived(cartItems, ($cartItems) => {
		return Object.values($cartItems).reduce(
			(accumulator: number, { quantity }) => accumulator + quantity,
			0
		);
	});

	const updateQuantity = (productId: string, difference: number) => {
		cartItems.update((currentCartItems) => ({
			...currentCartItems,
			[productId]: {
				...currentCartItems[productId],
				quantity: currentCartItems[productId].quantity + difference,
			},
		}));
	};

	const increaseQuantity = (
		productId: string,
		quantity: number,
		mixpanelEvents: CheckoutMixpanelEvents
	) => {
		updateQuantity(productId, quantity);
		const cartItem = get(cartItems)[productId];
		tagAddToCart(cartItem);

		track(mixpanelEvents.ChangeQuantity, {
			artworkId: cartItem.entityId,
			from: cartItem.quantity,
			to: cartItem.quantity + quantity,
			...(cartItem.contentKey || {}),
			...getDropTrackingFields(cartItem),
		});
	};

	const decreaseQuantity = (
		productId: string,
		mixpanelEvents: CheckoutMixpanelEvents
	) => {
		const cartItem = get(cartItems)[productId];
		if (cartItem.quantity <= 1) {
			return;
		}

		track(mixpanelEvents.ChangeQuantity, {
			artworkId: cartItem.entityId,
			from: cartItem.quantity,
			to: cartItem.quantity - 1,
			...(cartItem.contentKey || {}),
			...getDropTrackingFields(cartItem),
		});

		updateQuantity(productId, -1);
		tagRemoveFromCart(cartItem, 1);
	};

	const removeFromCart = (
		productId: string | null | undefined,
		mixpanelEvents: CheckoutMixpanelEvents
	) => {
		if (!productId) {
			return;
		}

		const cartItem = get(cartItems)[productId];

		// Callers may ask to remove a product that was never in this store (e.g.
		// the order-confirmed page clears applied items from the wishlist even
		// when they were not wishlisted). Nothing to remove or track then.
		if (!cartItem) {
			return;
		}

		cartItems.update((currentCartItems) => {
			const { [productId]: cartItemToRemove, ...restCurrentCartItems } =
				currentCartItems;

			return restCurrentCartItems;
		});

		if (
			// No items
			!Object.keys(get(cartItems)).length ||
			// No restricted item and no more restricted item in the cart
			(get(code) &&
				!get(code)?.amount &&
				Object.values(get(cartItems)).every(
					(cartItem) => !cartItem.restricted
				)) ||
			// No item related to the current coupon code
			Object.keys(get(cartItems)).every(
				(productId) => !(get(code)?.products || []).includes(productId)
			)
		) {
			clearCode();
		}

		track(mixpanelEvents.RemoveFromCart, {
			artworkId: cartItem.entityId,
			quantity: cartItem.quantity,
			...getDropTrackingFields(cartItem),
		});

		tagRemoveFromCart(cartItem, cartItem.quantity);
	};

	const addItemsToCart = (
		productIds: (string | null | undefined)[],
		cartItemsToAdd: Omit<CartItem, 'quantity'>[],
		mixpanelEvents: CheckoutMixpanelEvents,
		gridId: string
	) => {
		let newCartItems = { ...get(cartItems) };

		animated.set(gridId);

		setTimeout(() => {
			animated.set('');
			added.set(gridId);

			setTimeout(() => {
				added.set('');
			}, CART_ANIMATION_DURATION * 1000);
		}, CART_ANIMATION_DURATION * 1000);

		cartItemsToAdd.forEach((cartItemToAdd, i) => {
			const productId = productIds[i];
			if (!productId) {
				return;
			}

			if (newCartItems[productId]) {
				newCartItems = {
					...newCartItems,
					[productId]: {
						...newCartItems[productId],
						quantity: newCartItems[productId].quantity + 1,
					},
				};
			} else {
				newCartItems = {
					...newCartItems,
					[productId]: {
						...cartItemToAdd,
						quantity: 1,
					},
				};
			}
		});

		cartItems.set(newCartItems);

		track(mixpanelEvents.AddSetToCart, {
			artworkIds: cartItemsToAdd.map((cartItem) => cartItem.entityId),
			// A set spans several artworks, so it carries the same per-line items
			// array as the checkout events (HB-959). The quantity is 1 per line
			// here — a set adds one of each.
			items: getTrackedCartItems(
				cartItemsToAdd.map((cartItem) => ({ ...cartItem, quantity: 1 }))
			),
		});
	};

	const addToCart = (
		productId: string | null | undefined,
		cartItem: Omit<CartItem, 'quantity'>,
		quantity: number,
		mixpanelEvents: CheckoutMixpanelEvents
	) => {
		if (!productId) {
			return;
		}

		animated.set(productId);

		setTimeout(() => {
			animated.set('');
			added.set(productId);

			setTimeout(() => {
				added.set('');
			}, CART_ANIMATION_DURATION * 1000);
		}, CART_ANIMATION_DURATION * 1000);

		if (get(cartItems)[productId]) {
			increaseQuantity(productId, quantity, mixpanelEvents);
			return;
		}

		cartItems.update((currentCartItems) => ({
			...currentCartItems,
			[productId]: {
				quantity,
				...cartItem,
			},
		}));

		track(mixpanelEvents.AddToCart, {
			artworkId: cartItem.entityId,
			quantity,
			...(cartItem.contentKey || {}),
			...getDropTrackingFields(cartItem),
		});

		tagAddToCart(cartItem);
	};

	const handleCartUpdate = (response: { amount: { discount: number } }) => {
		const couponCode = getCouponCode();
		const couponProducts = getCouponProducts();
		if (couponCode) {
			code.set({
				products: couponProducts,
				code: couponCode,
				amount: response?.amount?.discount,
			});
		}
	};

	return {
		addItemsToCart,
		resetCartWithItems,
		refreshFromStorage,
		animated,
		added,
		debouncedCartItems,
		submittingCode,
		enablingCode,
		handleCartUpdate,
		code,
		getCouponCode,
		getCouponAmount,
		clearCode,
		addCode,
		increaseQuantity,
		decreaseQuantity,
		addToCart,
		clearCart,
		removeFromCart,
		cartItems,
		cartId,
		totalNbItems,
	};
};
